import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaSave, FaUndo, FaSearch, FaGlobe, FaTwitter } from 'react-icons/fa';
import { apiService } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { toast } from 'react-toastify';

const AdminSEO = () => {
  const [seo, setSeo] = useState({
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    og_title: '',
    og_description: '',
    og_image: '',
    twitter_card: 'summary_large_image',
    canonical_url: '',
    robots: 'index, follow',
    json_ld: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activePage, setActivePage] = useState('home');

  const pages = [
    { id: 'home', label: 'Home' },
    { id: 'about', label: 'About' },
    { id: 'projects', label: 'Projects' },
    { id: 'blog', label: 'Blog' },
    { id: 'services', label: 'Services' },
    { id: 'hire', label: 'Hire Me' },
    { id: 'contact', label: 'Contact' }
  ];

  useEffect(() => {
    fetchSEO();
  }, [activePage]);

  const fetchSEO = async () => {
    try {
      const response = await apiService.getSEO(activePage);
      setSeo(response.data || {
        meta_title: '',
        meta_description: '',
        meta_keywords: '',
        og_title: '',
        og_description: '',
        og_image: '',
        twitter_card: 'summary_large_image',
        canonical_url: '',
        robots: 'index, follow',
        json_ld: ''
      });
    } catch (error) {
      console.error('Error fetching SEO data:', error);
      toast.error('Failed to fetch SEO data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiService.updateSEO(activePage, seo);
      toast.success('SEO settings updated successfully');
    } catch (error) {
      toast.error('Failed to update SEO settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset changes?')) {
      fetchSEO();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white">SEO Settings</h1>
          <p className="text-gray-400 mt-1">Optimize your pages for search engines</p>
        </div>
      </div>

      {/* Page Selector */}
      <div className="flex flex-wrap gap-2 mb-6">
        {pages.map((page) => (
          <button
            key={page.id}
            onClick={() => setActivePage(page.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
              activePage === page.id
                ? 'bg-primary-500 text-white shadow-lg shadow-primary-500/30'
                : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
            }`}
          >
            {page.label}
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="glass rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <FaSearch className="text-primary-400" />
            Meta Tags
          </h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Meta Title
              </label>
              <input
                type="text"
                value={seo.meta_title || ''}
                onChange={(e) => setSeo({ ...seo, meta_title: e.target.value })}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Page title for search results"
              />
              <p className="text-xs text-gray-500 mt-1">
                {seo.meta_title?.length || 0}/60 characters recommended
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Meta Description
              </label>
              <textarea
                rows="2"
                value={seo.meta_description || ''}
                onChange={(e) => setSeo({ ...seo, meta_description: e.target.value })}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Page description for search results"
              />
              <p className="text-xs text-gray-500 mt-1">
                {seo.meta_description?.length || 0}/160 characters recommended
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Meta Keywords
              </label>
              <input
                type="text"
                value={seo.meta_keywords || ''}
                onChange={(e) => setSeo({ ...seo, meta_keywords: e.target.value })}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="keyword1, keyword2, keyword3"
              />
            </div>
          </div>
        </div>

        <div className="glass rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <FaGlobe className="text-primary-400" />
            Open Graph
          </h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                OG Title
              </label>
              <input
                type="text"
                value={seo.og_title || ''}
                onChange={(e) => setSeo({ ...seo, og_title: e.target.value })}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Title for social sharing"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                OG Description
              </label>
              <textarea
                rows="2"
                value={seo.og_description || ''}
                onChange={(e) => setSeo({ ...seo, og_description: e.target.value })}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Description for social sharing"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                OG Image URL
              </label>
              <input
                type="url"
                value={seo.og_image || ''}
                onChange={(e) => setSeo({ ...seo, og_image: e.target.value })}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="https://example.com/image.jpg"
              />
            </div>
          </div>
        </div>

        <div className="glass rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <FaTwitter className="text-primary-400" />
            Twitter Cards
          </h3>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Twitter Card Type
            </label>
            <select
              value={seo.twitter_card || 'summary_large_image'}
              onChange={(e) => setSeo({ ...seo, twitter_card: e.target.value })}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="summary">Summary</option>
              <option value="summary_large_image">Summary with Large Image</option>
            </select>
          </div>
        </div>

        <div className="glass rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Advanced Settings</h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Canonical URL
              </label>
              <input
                type="url"
                value={seo.canonical_url || ''}
                onChange={(e) => setSeo({ ...seo, canonical_url: e.target.value })}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="https://example.com/page"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Robots Meta
              </label>
              <input
                type="text"
                value={seo.robots || 'index, follow'}
                onChange={(e) => setSeo({ ...seo, robots: e.target.value })}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="index, follow"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                JSON-LD Structured Data
              </label>
              <textarea
                rows="4"
                value={seo.json_ld || ''}
                onChange={(e) => setSeo({ ...seo, json_ld: e.target.value })}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder='{"@context": "https://schema.org", "@type": "Person"}'
              />
            </div>
          </div>
        </div>

        <div className="flex gap-4 pt-4 border-t border-white/10">
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg font-medium text-white hover:shadow-lg hover:shadow-primary-500/30 transition-all duration-300 flex items-center gap-2 disabled:opacity-50"
          >
            <FaSave />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          <button
            type="button"
            onClick={handleReset}
            className="px-6 py-2 border border-white/10 rounded-lg font-medium text-gray-400 hover:bg-white/5 transition-all duration-300 flex items-center gap-2"
          >
            <FaUndo />
            Reset
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminSEO;